import type { CompanyNeedPaginatedResponse } from '@/modules/companyNeed/types';
import type {
	CandidatesQueryParams,
	CandidateWorker,
} from '@/modules/companyNeed/views/details/candidates/types';
import type { MatchingWorkerItemDto } from '@/modules/companyNeed/views/details/matching/types';
import type { Timestamp } from '@/types/common';

type CandidateWorkerApiItem = Partial<CandidateWorker> &
	Partial<MatchingWorkerItemDto> & {
		id?: string;
		date?: string | number | null;
		deactivationComment?: string;
		refusalReason?: string;
	};

type CandidateWorkersApiResponse = {
	items?: CandidateWorkerApiItem[];
	data?: CandidateWorkerApiItem[];
	offset?: number;
	limit?: number;
	total?: number;
	page?: number;
};

const toTimestamp = (value: string | number | null | undefined): Timestamp => {
	if (value == null || value === '') return 0;
	if (typeof value === 'number') return value;
	const parsed = new Date(value).getTime();
	return Number.isNaN(parsed) ? 0 : parsed;
};

export const mapCandidateWorkerApiItemToCandidateWorker = (
	item: CandidateWorkerApiItem
): CandidateWorker => ({
	id: item.id ?? item.talentId ?? '',
	firstname: item.firstname ?? '',
	lastname: item.lastname ?? '',
	jobs: Array.isArray(item.jobs) ? item.jobs : [],
	origin: Array.isArray(item.origin) ? item.origin : [],
	email: item.email ?? '',
	date: toTimestamp(item.date ?? item.lastUpdate),
	inPool: item.inPool ?? false,
	phone: item.phone ?? null,
	delegable: item.delegable ?? true,
	deactivationComment: item.deactivationComment,
	refusalReason: item.refusalReason,
});

export const mapCandidateWorkersApiResponseToPaginatedCandidates = (
	response: CandidateWorkersApiResponse,
	queryParams: CandidatesQueryParams
): CompanyNeedPaginatedResponse<CandidateWorker> => {
	const rawItems = response.items ?? response.data ?? [];
	const limit = response.limit ?? queryParams.limit ?? rawItems.length;
	const offset = response.offset ?? queryParams.offset ?? 0;
	const total = response.total ?? rawItems.length;
	const page =
		response.page ?? (limit > 0 ? Math.floor(offset / limit) + 1 : 1);
	const totalPages = limit > 0 ? Math.ceil(total / limit) : 0;

	return {
		items: rawItems.map(mapCandidateWorkerApiItemToCandidateWorker),
		offset,
		limit,
		page,
		total,
		totalPages,
		hasNextPage: offset + limit < total,
		hasPreviousPage: offset > 0,
	};
};
